import * as React from "react";
import Box from "@mui/material/Box";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import {
  TextField,
  Popover,
  Button,
  Card,
  CardContent,
  Typography,
} from "@mui/material";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { Calendar } from "lucide-react";

const ReceiptFilters = ({ onFilterChange, filters }) => {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);
  const [tempFromDate, setTempFromDate] = React.useState(
    filters.fromDate ? dayjs(filters.fromDate, "DD/MM/YYYY") : null,
  );
  const [tempToDate, setTempToDate] = React.useState(
    filters.toDate ? dayjs(filters.toDate, "DD/MM/YYYY") : null,
  );

  const handleFilterChange = (field, value) => {
    const newFilters = { ...filters, [field]: value };
    onFilterChange?.(newFilters);
  };

  // Date range filter handlers
  const handleOpenDateRange = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseDateRange = () => {
    setAnchorEl(null);
  };

  const handleApplyDateRange = () => {
    const fromDateStr = tempFromDate ? tempFromDate.format("DD/MM/YYYY") : "";
    const toDateStr = tempToDate ? tempToDate.format("DD/MM/YYYY") : "";

    // Apply both changes at once instead of separately
    const newFilters = {
      ...filters,
      fromDate: fromDateStr,
      toDate: toDateStr,
    };

    onFilterChange?.(newFilters);
    handleCloseDateRange();

    // Log the new values for debugging
    console.log("Applied date range:", {
      fromDate: fromDateStr,
      toDate: toDateStr,
    });
  };

  const handleClearDateRange = () => {
    setTempFromDate(null);
    setTempToDate(null);

    // Clear both date values in a single update
    const newFilters = {
      ...filters,
      fromDate: "",
      toDate: "",
    };

    onFilterChange?.(newFilters);
    handleCloseDateRange();
  };

  // Format the date range for display
  const formatDateRange = () => {
    // Add debugging to see the actual values
    console.log("Date Range Values:", {
      fromDate: filters.fromDate,
      toDate: filters.toDate,
    });

    if (filters.fromDate && filters.toDate) {
      return `${filters.fromDate} to ${filters.toDate}`;
    } else if (filters.fromDate) {
      return `From ${filters.fromDate}`;
    } else if (filters.toDate) {
      return `Until ${filters.toDate}`;
    }
    return "Select Date Range";
  };

  return (
    <div className="flex items-center gap-4 flex-wrap">
      {/* Payment Mode Filter */}
      <FormControl size="small" sx={{ minWidth: 180 }}>
        <InputLabel>Payment Mode</InputLabel>
        <Select
          value={filters.paymentMode}
          label="Payment Mode"
          onChange={e => handleFilterChange("paymentMode", e.target.value)}
          sx={{
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "#E5E7EB",
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: "#5652B0",
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: "#5652B0",
            },
          }}>
          <MenuItem value="">All</MenuItem>
          <MenuItem value="Cash">Cash</MenuItem>
          <MenuItem value="Bank">Bank</MenuItem>
        </Select>
      </FormControl>

      {/* Date Range Filter */}
      <div className="relative">
        <Button
          onClick={handleOpenDateRange}
          variant="outlined"
          size="small"
          startIcon={<Calendar size={16} />}
          sx={{
            minWidth: 180,
            height: 40,
            justifyContent: "flex-start",
            textTransform: "none",
            borderColor: "#E5E7EB",
            color: filters.fromDate || filters.toDate ? "#374151" : "#83899F",
            "&:hover": {
              borderColor: "#5652B0",
              backgroundColor: "#F9FAFB",
            },
            paddingLeft: 2,
            fontFamily: "Outfit, sans-serif",
          }}>
          {formatDateRange()}
        </Button>
        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handleCloseDateRange}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
          PaperProps={{
            sx: { mt: 1, boxShadow: 3 },
          }}>
          <Card sx={{ minWidth: 320, boxShadow: "none" }}>
            <CardContent>
              <Typography
                variant="subtitle2"
                gutterBottom
                sx={{ fontFamily: "Outfit, sans-serif", fontWeight: 500 }}>
                Select Date Range
              </Typography>
              <div className="flex flex-col gap-4 mt-3">
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="From Date"
                    value={tempFromDate}
                    onChange={setTempFromDate}
                    slotProps={{
                      textField: {
                        size: "small",
                        fullWidth: true,
                        sx: {
                          "& .MuiOutlinedInput-root": {
                            "& fieldset": {
                              borderColor: "#E5E7EB",
                            },
                            "&:hover fieldset": {
                              borderColor: "#5652B0",
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: "#5652B0",
                            },
                          },
                        },
                      },
                    }}
                  />
                </LocalizationProvider>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="To Date"
                    value={tempToDate}
                    onChange={setTempToDate}
                    slotProps={{
                      textField: {
                        size: "small",
                        fullWidth: true,
                        sx: {
                          "& .MuiOutlinedInput-root": {
                            "& fieldset": {
                              borderColor: "#E5E7EB",
                            },
                            "&:hover fieldset": {
                              borderColor: "#5652B0",
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: "#5652B0",
                            },
                          },
                        },
                      },
                    }}
                  />
                </LocalizationProvider>
              </div>
              <div className="flex justify-between mt-4">
                <Button
                  variant="text"
                  onClick={handleClearDateRange}
                  sx={{
                    textTransform: "none",
                    color: "#6B7280",
                    fontFamily: "Outfit, sans-serif",
                  }}>
                  Clear
                </Button>
                <Button
                  variant="contained"
                  onClick={handleApplyDateRange}
                  sx={{
                    textTransform: "none",
                    backgroundColor: "#5652B0",
                    "&:hover": {
                      backgroundColor: "#4A47A3",
                    },
                    fontFamily: "Outfit, sans-serif",
                  }}>
                  Apply
                </Button>
              </div>
            </CardContent>
          </Card>
        </Popover>
      </div>

      {/* Amount Filter */}
      <FormControl size="small" sx={{ minWidth: 180 }}>
        <InputLabel>Amount Range</InputLabel>
        <Select
          value={filters.amount}
          label="Amount Range"
          onChange={e => handleFilterChange("amount", e.target.value)}
          sx={{
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "#E5E7EB",
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: "#5652B0",
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: "#5652B0",
            },
          }}>
          <MenuItem value="">All</MenuItem>
          <MenuItem value="0-10000">₹0 - ₹10,000</MenuItem>
          <MenuItem value="10000-25000">₹10,000 - ₹25,000</MenuItem>
          <MenuItem value="25000-50000">₹25,000 - ₹50,000</MenuItem>
          <MenuItem value="50000+">₹50,000+</MenuItem>
        </Select>
      </FormControl>

      {/* Sort By Filter */}
      <FormControl size="small" sx={{ minWidth: 180 }}>
        <InputLabel>Sort By</InputLabel>
        <Select
          value={filters.sortBy}
          label="Sort By"
          onChange={e => handleFilterChange("sortBy", e.target.value)}
          sx={{
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "#E5E7EB",
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: "#5652B0",
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: "#5652B0",
            },
          }}>
          <MenuItem value="">None</MenuItem>
          <MenuItem value="date_desc">Date (Newest First)</MenuItem>
          <MenuItem value="date_asc">Date (Oldest First)</MenuItem>
          <MenuItem value="amount_desc">Amount (High-Low)</MenuItem>
          <MenuItem value="amount_asc">Amount (Low-High)</MenuItem>
          <MenuItem value="firm_asc">Firm Name (A-Z)</MenuItem>
          <MenuItem value="firm_desc">Firm Name (Z-A)</MenuItem>
        </Select>
      </FormControl>
    </div>
  );
};

export default ReceiptFilters;
